import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Mutex } from 'async-mutex';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FileStorageInjectionToken, FileStorageServiceInterface } from '../../interfaces/file-storage.interface';
import { AddressModel, AddressModelFromJson, JsonAddressModel } from '../../models/complex/address.model';
import { JsonTimesheetModel, TimesheetModel, TimesheetModelFromJson } from '../../models/low-level/timesheet.model';
import { assertUnreachable } from '../../utilities/assert-unreachable';
import { isFileError } from '../../utilities/is-file-error';
import { StorageTimesheetServiceInterface } from './storage-timesheet.interface';
import { TIMESHEET_KEYS } from './timesheet.enum';

const KEY_ADDRESSES = 'addressCollection';

@Injectable({
    providedIn: 'root',
})
export class TimesheetIndexedDbStorageService implements StorageTimesheetServiceInterface {
    private readonly currentTimesheetMutex: Mutex;
    private readonly submittedTimesheetMutex: Mutex;
    private readonly allTimesheetMutex: Mutex;
    private readonly addressesMutex: Mutex;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(FileStorageInjectionToken) private readonly fileStorageService: FileStorageServiceInterface,
    ) {
        this.currentTimesheetMutex = new Mutex();
        this.submittedTimesheetMutex = new Mutex();
        this.allTimesheetMutex = new Mutex();
        this.addressesMutex = new Mutex();
    }

    public getTimesheet(key: TIMESHEET_KEYS): Observable<TimesheetModel> {
        return this.getTimesheetByKey(key);
    }

    public saveTimesheet(key: TIMESHEET_KEYS, timesheet: TimesheetModel): Observable<TimesheetModel> {
        return this.saveTimesheetByKey(key, timesheet);
    }

    public getAllTimesheets(): Observable<TimesheetModel[]> {
        return this.fileStorageService
            .loadCompressedData<JsonTimesheetModel[]>(this.allTimesheetMutex, TIMESHEET_KEYS.ALL_TIMESHEET)
            .pipe(
                map((jsonTimesheets) =>
                    jsonTimesheets.map((jsonTimesheet) => new TimesheetModelFromJson(jsonTimesheet)),
                ),
                catchError((error: unknown) => {
                    if (isFileError(error) && error.code === 1) {
                        this.log.error('LocalStorageTimesheetService: getAllTimesheets: error file not found');
                        return of([]);
                    }

                    throw error;
                }),
            );
    }

    public saveAllTimesheets(timesheet: TimesheetModel[]): Observable<void> {
        return this.fileStorageService
            .storeCompressedData(this.allTimesheetMutex, TIMESHEET_KEYS.ALL_TIMESHEET, timesheet)
            .pipe(map(() => undefined));
    }

    public getAddresses(): Observable<AddressModelFromJson[]> {
        return this.fileStorageService.loadCompressedData<JsonAddressModel[]>(this.addressesMutex, KEY_ADDRESSES).pipe(
            map((jsonAddresses) => jsonAddresses.map((jsonAddress) => new AddressModelFromJson(jsonAddress))),
            catchError((error) => {
                this.log.debug(
                    'LocalStorageTimesheetService: getAddresses: Retrieving addresses from file storage was not possible: ',
                    error,
                );
                return of([]);
            }),
        );
    }

    public saveAddresses(addresses: AddressModel[]): Observable<void> {
        return this.fileStorageService
            .storeCompressedData(this.addressesMutex, KEY_ADDRESSES, addresses)
            .pipe(map(() => undefined));
    }

    // PRIVATE

    private getTimesheetByKey(key: TIMESHEET_KEYS): Observable<TimesheetModel> {
        let mutex: Mutex;
        switch (key) {
            case TIMESHEET_KEYS.CURRENT_TIMESHEET:
                mutex = this.currentTimesheetMutex;
                break;
            case TIMESHEET_KEYS.SUBMITTED_TIMESHEET:
                mutex = this.submittedTimesheetMutex;
                break;
            case TIMESHEET_KEYS.ALL_TIMESHEET:
                mutex = this.allTimesheetMutex;
                break;
            default:
                mutex = new Mutex();
                assertUnreachable(key);
        }
        return this.fileStorageService.loadCompressedDataWithTimestamp<JsonTimesheetModel>(mutex, key).pipe(
            map((jsonTimeSheet) => new TimesheetModelFromJson(jsonTimeSheet.data)),
            catchError((error) => {
                if (isFileError(error) && error.code === 1) {
                    this.log.debug(
                        `LocalStorageTimesheetService: getTimesheetByKey : ${key} file not found, returning empty timesheet model`,
                    );
                    const emptyTimesheet = new TimesheetModel(moment.invalid(), [], [], [], moment.invalid(), false);
                    return this.saveTimesheetByKey(key, emptyTimesheet);
                }
                throw error;
            }),
        );
    }

    private saveTimesheetByKey(key: TIMESHEET_KEYS, timesheet: TimesheetModel): Observable<TimesheetModel> {
        let mutex: Mutex;
        switch (key) {
            case TIMESHEET_KEYS.CURRENT_TIMESHEET:
                mutex = this.currentTimesheetMutex;
                break;
            case TIMESHEET_KEYS.SUBMITTED_TIMESHEET:
                mutex = this.submittedTimesheetMutex;
                break;
            case TIMESHEET_KEYS.ALL_TIMESHEET:
                mutex = this.allTimesheetMutex;
                break;
            default:
                mutex = new Mutex();
                assertUnreachable(key);
        }
        return this.fileStorageService.storeCompressedData(mutex, key, timesheet, timesheet.saveTimestamp);
    }
}
